import React, { useMemo, useState } from 'react';
import { Radio, ChevronDown, ChevronUp, Star } from 'lucide-react';

/**
 * Spotter panel — collapsible section rendered above the main telemetry
 * table. Reads `spotterEventsRef` (last N laps per driver) + the
 * `focused` Set (starred drivers), builds a "Faltó en S1, mejoraste S3"
 * message from raw sector deltas. Pure client-side comparison — no LLM.
 *
 * Design:
 *  - Shows the LATEST closed lap per focused driver, most recent first.
 *  - If no drivers are starred, shows a hint prompting to star one.
 *  - Each row: driver number + name, lap#, lap time, then a plain-text
 *    Spanish message describing per-sector deltas vs personal-best.
 *  - Colour coding: red = perdió tiempo, green = ganó, grey = igualó.
 */

const fmtDelta = (secs) => {
  if (secs == null) return '';
  const abs = Math.abs(secs);
  return `${abs.toFixed(3)}s`;
};

const fmtLap = (secs) => {
  if (secs == null || !Number.isFinite(secs) || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs - m * 60;
  return m > 0
    ? `${m}:${s.toFixed(3).padStart(6, '0')}`
    : s.toFixed(3);
};

/**
 * Build a human-readable Spanish sentence from the deltas array.
 * Example: "Perdiste 0.213s en S1 · Mejoraste 0.045s en S3"
 * Only mentions sectors with a meaningful delta (>= 0.01 s). If nothing
 * moves, falls back to "Vuelta plana vs tu mejor".
 */
const buildMessage = (deltas) => {
  const parts = [];
  (deltas || []).forEach((d, i) => {
    if (!d) return;
    if (d.kind === 'first') {
      parts.push({ kind: 'first', label: `S${i + 1} inicial ${d.current.toFixed(3)}s` });
    } else if (Math.abs(d.delta) < 0.01) {
      // Ignored — too small to be trackside-actionable.
    } else if (d.delta < 0) {
      parts.push({ kind: 'gain', label: `Mejoraste ${fmtDelta(d.delta)} en S${i + 1}` });
    } else {
      parts.push({ kind: 'loss', label: `Perdiste ${fmtDelta(d.delta)} en S${i + 1}` });
    }
  });
  if (parts.length === 0) return { flat: true, parts: [] };
  return { flat: false, parts };
};

const SpotterPanel = ({ focused, spotterEventsRef, spotterTick, participantsByNumber }) => {
  const [collapsed, setCollapsed] = useState(false);

  // Latest event per focused driver — computed on every spotterTick.
  const items = useMemo(() => {
    const focusedArr = Array.from(focused || []);
    const out = [];
    for (const dn of focusedArr) {
      const events = spotterEventsRef.current?.[String(dn)] || [];
      const latest = events[0];
      if (latest) {
        out.push({ ...latest, driver_number: dn });
      } else {
        // Focused but hasn't closed a lap yet in the session.
        out.push({
          driver_number: dn,
          pending: true,
          driver_name: participantsByNumber?.[String(dn)]?.name || `#${dn}`,
        });
      }
    }
    // Most-recently-closed lap first.
    out.sort((a, b) => (b.t || 0) - (a.t || 0));
    return out;
  }, [focused, spotterTick, participantsByNumber, spotterEventsRef]);

  return (
    <div
      data-testid="spotter-panel"
      className="rounded-lg overflow-hidden"
      style={{ backgroundColor: 'var(--dp-surface-0)', border: '1px solid var(--dp-border)' }}
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.02]"
        data-testid="spotter-toggle"
      >
        <Radio className="w-4 h-4 text-red-400" />
        <span className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--dp-text-primary)' }}>
          Spotter
        </span>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: 'var(--dp-text-muted)', backgroundColor: 'var(--dp-surface-1)' }}>
          {focused?.size || 0} favorito{focused?.size === 1 ? '' : 's'}
        </span>
        <span className="ml-auto text-gray-400">
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </span>
      </button>
      {!collapsed && (
        <div className="px-3 pb-3 pt-1 space-y-2">
          {items.length === 0 && (
            <div className="text-xs text-gray-400 py-2 flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5" />
              Marcá con ★ un piloto en la tabla de abajo para recibir mensajes de
              spotter al cierre de cada vuelta.
            </div>
          )}
          {items.map((it) => {
            if (it.pending) {
              return (
                <div
                  key={`sp-${it.driver_number}`}
                  className="text-xs text-gray-400 py-1"
                  data-testid={`spotter-pending-${it.driver_number}`}
                >
                  <span className="font-bold text-white">#{it.driver_number} {it.driver_name}</span>
                  {' · '}Esperando cierre de la primera vuelta…
                </div>
              );
            }
            const msg = buildMessage(it.sector_deltas);
            return (
              <div
                key={`sp-${it.driver_number}-${it.lap_number}`}
                data-testid={`spotter-event-${it.driver_number}`}
                className="rounded-md px-3 py-2"
                style={{ backgroundColor: 'var(--dp-surface-1)', border: '1px solid var(--dp-border)' }}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-yellow-400">
                    <Star className="w-3 h-3" fill="currentColor" />
                    #{it.driver_number}
                  </span>
                  <span className="text-sm font-semibold text-white truncate max-w-[220px]">{it.driver_name}</span>
                  <span className="text-[11px] text-gray-400">
                    · Vuelta {it.lap_number} · <span className="font-mono">{fmtLap(it.lap_time)}</span>
                  </span>
                </div>
                <div className="mt-1.5 text-sm leading-relaxed">
                  {msg.flat ? (
                    <span className="text-gray-400 italic">Vuelta plana vs tu mejor.</span>
                  ) : (
                    msg.parts.map((p, i) => (
                      <span key={`p-${i}`} className="inline-block mr-2">
                        {p.kind === 'gain' && <span className="text-green-400 font-semibold">✓ {p.label}</span>}
                        {p.kind === 'loss' && <span className="text-red-400 font-semibold">✗ {p.label}</span>}
                        {p.kind === 'first' && <span className="text-purple-300 font-semibold">◆ {p.label}</span>}
                        {i < msg.parts.length - 1 && <span className="text-gray-600 mx-1">·</span>}
                      </span>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SpotterPanel;
